import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';

/**
 * Send / confirm the code for an Email Verification step.
 *
 * Both hooks work in the authenticated run AND the anonymous shared-link run, choosing their client
 * and path the same way `useSessionQuestionnaire` and `useVerifyStep` do. On the public route the
 * tenant and session are resolved server-side from the run token — nothing here supplies a tenant.
 *
 * NEITHER OF THESE COMPLETES THE STEP. Confirming the code records the evidence server-side; the
 * runner then submits the step through the normal verify path, which re-checks it. See
 * `VerifySessionEmailOtpCommandHandler` for why it is split that way.
 */

const emailOtpSentSchema = z.object({
  /** Masked, e.g. "s***m@signcare.io" — the server never echoes the full address back. */
  maskedEmail: z.string(),
  validityMinutes: z.number(),
  resendCooldownSeconds: z.number(),
});

const emailOtpVerifiedSchema = z.object({
  /** The address the server confirmed — read from the challenge, not from what we posted. */
  email: z.string(),
  verifiedAt: z.string(),
});

export type EmailOtpSent = z.infer<typeof emailOtpSentSchema>;
export type EmailOtpVerified = z.infer<typeof emailOtpVerifiedSchema>;

/** Builds the authed or public path for one of the two email-OTP actions. */
function useEmailOtpEndpoint(sessionId: string | undefined, stepId: string) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();
  const token = publicRun?.token;

  return (action: 'request' | 'verify') => ({
    client: token ? publicApiClient : apiClient,
    path: token
      ? `api/v1/public/sessions/${token}/steps/${stepId}/email-otp/${action}`
      : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/email-otp/${action}`,
  });
}

export function useRequestSessionEmailOtp(sessionId: string | undefined, stepId: string) {
  const endpoint = useEmailOtpEndpoint(sessionId, stepId);

  return useMutation({
    mutationFn: async (email: string) => {
      const { client, path } = endpoint('request');
      const raw = await client.post(path, { json: { email } }).json();
      return emailOtpSentSchema.parse(raw);
    },
  });
}

export function useVerifySessionEmailOtp(sessionId: string | undefined, stepId: string) {
  const endpoint = useEmailOtpEndpoint(sessionId, stepId);

  return useMutation({
    // No email in the body: the address being confirmed is the one the code was issued to, held
    // server-side on the challenge.
    mutationFn: async (code: string) => {
      const { client, path } = endpoint('verify');
      const raw = await client.post(path, { json: { code } }).json();
      return emailOtpVerifiedSchema.parse(raw);
    },
  });
}
